import type { PlaybookCriterionNode } from "@repo/app-types/entities/playbook";
import type { SampleToolResultCriterionGradingsPayload } from "@repo/app-types/entities/playbookNodeSampleToolResult";
import { Size } from "@repo/ui/helpers/helpers";
import { SmallTabs } from "@repo/ui/Navigation/smallTabs";
import { SlideoverContainer } from "@repo/ui/Slideover/slideoverContainer";
import {
  SlideoverDialog,
  SlideoverDialogContent,
  SlideoverDialogTitleHeader,
} from "@repo/ui/Slideover/slideoverDialog";
import type { FC } from "react";
import { FeedbackGradingsTable } from "./feedbackGradingsTable.js";
import { SampleGradingsTable } from "./sampleGradingsTable.js";

const GRADED_CALLS_TABS = [
  { id: "sample", label: "Sample calls" },
  { id: "feedback", label: "Feedback calls" },
] as const;

export type GradedCallsTab = (typeof GRADED_CALLS_TABS)[number]["id"];

interface GradedCallsSlideoverProps {
  isOpen: boolean;
  onClose: () => void;
  selectedTab: GradedCallsTab;
  onSelectTab: (tab: GradedCallsTab) => void;
  gradingValueType: PlaybookCriterionNode["grading_value_type"];
  gradings: SampleToolResultCriterionGradingsPayload | undefined;
  isLoading: boolean;
  isError: boolean;
}

export const GradedCallsSlideover: FC<GradedCallsSlideoverProps> = ({
  isOpen,
  onClose,
  selectedTab,
  onSelectTab,
  gradingValueType,
  gradings,
  isLoading,
  isError,
}) => {
  return (
    <SlideoverContainer
      isOpen={isOpen}
      onOpenChange={(newIsOpen) => {
        if (!newIsOpen) {
          onClose();
        }
      }}
      isDismissable
      size={Size.LARGE}
    >
      <SlideoverDialog label="Graded calls">
        <SlideoverDialogTitleHeader>Graded calls</SlideoverDialogTitleHeader>
        <SlideoverDialogContent>
          <div className="px-4 py-2">
            <SmallTabs
              tabs={GRADED_CALLS_TABS.map((tab) => tab.label)}
              selectedTabIndex={GRADED_CALLS_TABS.findIndex(
                (tab) => tab.id === selectedTab,
              )}
              onSelectTabIndex={(index) => {
                const tab = GRADED_CALLS_TABS[index];
                if (tab) {
                  onSelectTab(tab.id);
                }
              }}
            />
          </div>
          {selectedTab === "sample" ? (
            <>
              <p className="px-4 py-2 text-sm text-muted-foreground">
                Calls automatically selected and regraded to test this criteria
                update against the current baseline.
              </p>
              <SampleGradingsTable
                gradingValueType={gradingValueType}
                entries={gradings?.sample_grades ?? []}
                isLoading={isLoading}
                isError={isError}
              />
            </>
          ) : (
            <>
              <p className="px-4 py-2 text-sm text-muted-foreground">
                Calls regraded against grading feedback left by your teammates.
              </p>
              <FeedbackGradingsTable
                gradingValueType={gradingValueType}
                entries={gradings?.feedback_grades ?? []}
                isLoading={isLoading}
                isError={isError}
              />
            </>
          )}
        </SlideoverDialogContent>
      </SlideoverDialog>
    </SlideoverContainer>
  );
};
